import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout.jsx';
import { api, uploadUrl } from '../../api/client.js';
import { Swal2 } from '../../lib/swal.js';
import { MORPH_BTN_SM } from '../../lib/ui.js';

const FILTERS = [
  { id: 'all', label: 'Semua', icon: 'apps' },
  { id: 'active', label: 'Aktif', icon: 'visibility' },
  { id: 'inactive', label: 'Nonaktif', icon: 'visibility_off' },
];

// Bold-kan bagian teks yang cocok dengan kata pencarian
function highlightMatch(text, query) {
  if (!text) return text;
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <b className="font-bold text-neutral-900 dark:text-white">{text.slice(idx, idx + query.length)}</b>
      {text.slice(idx + query.length)}
    </>
  );
}

export default function CarouselList() {
  const [items, setItems] = useState(null);

  const [filter, setFilter] = useState('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const filterMenuRef = useRef(null);

  const [view, setView] = useState('grid');
  const viewContainerRef = useRef(null);
  const viewBtnRefs = useRef({});
  const [viewIndicator, setViewIndicator] = useState({ left: 0, width: 0 });

  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const searchBoxRef = useRef(null);

  const load = () => api.get('/admin/carousel').then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);

  useLayoutEffect(() => {
    const measure = () => {
      const btn = viewBtnRefs.current[view];
      const container = viewContainerRef.current;
      if (!btn || !container) return;
      const cRect = container.getBoundingClientRect();
      const bRect = btn.getBoundingClientRect();
      setViewIndicator({ left: bRect.left - cRect.left, width: bRect.width });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [view]);

  // Autocomplete: debounce ketik, saring dari data carousel (dari database) yang sudah dimuat
  useEffect(() => {
    const q = searchInput.trim().toLowerCase();
    if (!q || !items) { setSuggestions([]); return; }
    const t = setTimeout(() => {
      const matches = items.filter((item) => item.title?.toLowerCase().includes(q) || item.subtitle?.toLowerCase().includes(q));
      setSuggestions(matches.slice(0, 6));
      setActiveIdx(-1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, items]);

  // Tutup dropdown saat klik di luar
  useEffect(() => {
    const onClickOutside = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowSuggest(false);
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) setFilterOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const pickSuggestion = (item) => {
    setSearchInput(item.title);
    setSearch(item.title);
    setShowSuggest(false);
  };

  const onSearchKeyDown = (e) => {
    if (!showSuggest || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      pickSuggestion(suggestions[activeIdx]);
    } else if (e.key === 'Escape') {
      setShowSuggest(false);
    }
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setShowSuggest(false);
  };

  const q = search.trim().toLowerCase();
  const filtered = items
    ?.filter((item) => (filter === 'all' ? true : filter === 'active' ? item.is_active : !item.is_active))
    ?.filter((item) => (!q ? true : item.title?.toLowerCase().includes(q) || item.subtitle?.toLowerCase().includes(q)));

  const remove = async (item) => {
    const confirmed = await Swal2.confirmDelete(item.title || 'slide ini');
    if (!confirmed) return;
    await api.delete(`/admin/carousel/${item.id}`);
    Swal2.toast('success', 'Slide carousel berhasil dihapus.');
    load();
  };

  return (
    <AdminLayout title="Hero Carousel">
      <div className="flex flex-wrap items-center gap-2.5 mb-5">
        {/* Filter status: ikon corong + dropdown */}
        <div ref={filterMenuRef} className="relative flex-shrink-0">
          <button
            type="button"
            onClick={() => setFilterOpen((o) => !o)}
            aria-pressed={filterOpen}
            title="Filter status"
            className={`relative flex items-center justify-center w-10 h-10 rounded-xl border ${MORPH_BTN_SM}`}
            style={{
              borderColor: filter !== 'all' ? 'var(--accent)' : 'color-mix(in oklab, var(--accent) 20%, transparent)',
              background: filter !== 'all' ? 'color-mix(in oklab, var(--accent) 10%, transparent)' : 'white',
              color: 'var(--accent)',
            }}
          >
            <span className="material-symbols-outlined text-lg">filter_alt</span>
            {filter !== 'all' && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full ring-2 ring-white dark:ring-neutral-900" style={{ background: 'var(--accent)' }} />}
          </button>

          <div className={`filter-dropdown ${filterOpen ? 'filter-dropdown-open' : 'filter-dropdown-closed'} absolute z-30 top-[calc(100%+6px)] left-0 w-44 origin-top-left rounded-xl border bg-white dark:bg-neutral-800 shadow-xl overflow-hidden p-1`} style={{ borderColor: 'color-mix(in oklab, var(--accent) 20%, transparent)' }}>
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => { setFilter(f.id); setFilterOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-semibold text-left transition-colors ${filter === f.id ? '' : 'hover:bg-neutral-50 dark:hover:bg-neutral-700/50 text-neutral-600 dark:text-neutral-300'}`}
                style={filter === f.id ? { background: 'color-mix(in oklab, var(--accent) 12%, transparent)', color: 'var(--accent)' } : undefined}
              >
                <span className="material-symbols-outlined text-base">{f.icon}</span>
                {f.label}
                {filter === f.id && <span className="material-symbols-outlined text-base ml-auto">check</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Search bar */}
        <form onSubmit={submitSearch} className="flex-shrink-0">
          <div ref={searchBoxRef} className="relative">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-lg">search</span>
            <input
              value={searchInput}
              onChange={(e) => { setSearchInput(e.target.value); setShowSuggest(true); }}
              onFocus={() => { if (searchInput.trim()) setShowSuggest(true); }}
              onKeyDown={onSearchKeyDown}
              placeholder="Cari judul atau subjudul..."
              autoComplete="off"
              className="w-56 sm:w-72 h-10 pl-10 pr-4 rounded-xl border text-xs outline-none bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white border-neutral-200 dark:border-neutral-700 focus:border-forest-500 focus:ring-2 focus:ring-forest-500/20 transition-all"
            />

            {showSuggest && searchInput.trim() && (
              <div className="absolute z-30 top-[calc(100%+6px)] left-0 w-full sm:w-80 rounded-xl border bg-white dark:bg-neutral-800 shadow-xl overflow-hidden" style={{ borderColor: 'color-mix(in oklab, var(--accent) 20%, transparent)' }}>
                {suggestions.length === 0 ? (
                  <div className="px-4 py-3 text-xs text-neutral-400">Tidak ada hasil untuk "{searchInput}"</div>
                ) : (
                  suggestions.map((item, i) => (
                    <button
                      type="button"
                      key={item.id}
                      onMouseEnter={() => setActiveIdx(i)}
                      onClick={() => pickSuggestion(item)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${i === activeIdx ? 'bg-neutral-100 dark:bg-neutral-700' : 'hover:bg-neutral-50 dark:hover:bg-neutral-700/50'}`}
                    >
                      {item.image ? (
                        <img src={uploadUrl(item.image)} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                      ) : (
                        <span className="material-symbols-outlined text-neutral-400 text-base flex-shrink-0">image</span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs font-semibold text-neutral-700 dark:text-neutral-200 truncate">
                          {highlightMatch(item.title, searchInput)}
                        </span>
                        {item.subtitle && (
                          <span className="block text-[11px] text-neutral-400 truncate">{highlightMatch(item.subtitle, searchInput)}</span>
                        )}
                      </span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </form>

        {/* Toggle Grid/Tabel */}
        <div ref={viewContainerRef} className="relative inline-flex items-center gap-1 p-1 h-10 rounded-xl border bg-white dark:bg-neutral-900 flex-shrink-0" style={{ borderColor: 'color-mix(in oklab, var(--accent) 20%, transparent)' }}>
          <div
            className="absolute top-1 bottom-1 left-0 rounded-lg transition-transform duration-300 will-change-transform"
            style={{ background: 'var(--accent)', width: viewIndicator.width, transform: `translateX(${viewIndicator.left}px)`, transitionTimingFunction: 'var(--ease-spring)' }}
          />
          <button
            type="button"
            ref={(el) => { viewBtnRefs.current.grid = el; }}
            onClick={() => setView('grid')}
            aria-pressed={view === 'grid'}
            title="Tampilan grid"
            className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-lg ${view === 'grid' ? '' : 'text-[#262626] dark:text-neutral-300'} ${MORPH_BTN_SM}`}
            style={{ color: view === 'grid' ? '#fff' : undefined, transitionProperty: 'color, border-radius, transform' }}
          >
            <span className="material-symbols-outlined text-lg">grid_view</span>
          </button>
          <button
            type="button"
            ref={(el) => { viewBtnRefs.current.table = el; }}
            onClick={() => setView('table')}
            aria-pressed={view === 'table'}
            title="Tampilan tabel"
            className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-lg ${view === 'table' ? '' : 'text-[#262626] dark:text-neutral-300'} ${MORPH_BTN_SM}`}
            style={{ color: view === 'table' ? '#fff' : undefined, transitionProperty: 'color, border-radius, transform' }}
          >
            <span className="material-symbols-outlined text-lg">view_list</span>
          </button>
        </div>

        <Link to="/admin/carousel/new" className={`inline-flex items-center gap-2 px-5 h-10 rounded-xl font-bold text-white text-sm ml-auto flex-shrink-0 ${MORPH_BTN_SM}`} style={{ background: 'var(--accent)' }}>
          <span className="material-symbols-outlined text-base">add</span> Tambah Slide
        </Link>
      </div>

      {items === null && <p className="text-neutral-400">Memuat...</p>}
      {items !== null && filtered?.length === 0 && <p className="text-neutral-400">Tidak ada slide untuk filter/pencarian ini.</p>}

      {view === 'grid' ? (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered?.map((item) => (
          <div key={item.id} className="rounded-2xl overflow-hidden border bg-white dark:bg-neutral-900 border-black/5 dark:border-white/5">
            <div className="h-36 bg-neutral-100 dark:bg-neutral-800">
              {item.image ? <img src={uploadUrl(item.image)} className="w-full h-full object-cover" /> : (
                <div className="w-full h-full flex items-center justify-center text-neutral-300"><span className="material-symbols-outlined text-4xl">image</span></div>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${item.is_active ? 'bg-forest-500/10 text-forest-500' : 'bg-neutral-100 text-neutral-400 dark:bg-neutral-800'}`}>
                  {item.is_active ? 'Aktif' : 'Nonaktif'}
                </span>
                <span className="text-xs text-neutral-400">Urutan #{item.sort_order}</span>
              </div>
              <h4 className="font-bold text-sm text-neutral-900 dark:text-white line-clamp-2">{item.title}</h4>
              <div className="flex gap-2 mt-3">
                <Link to={`/admin/carousel/${item.id}/edit`} className={`flex-1 text-center text-xs font-semibold py-2 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-white ${MORPH_BTN_SM}`}>Edit</Link>
                <button onClick={() => remove(item)} className={`flex-1 text-xs font-semibold py-2 rounded-lg bg-red-50 text-red-500 dark:bg-red-950/30 ${MORPH_BTN_SM}`}>Hapus</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      ) : (
        <div className="rounded-2xl border bg-white dark:bg-neutral-900 border-black/5 dark:border-white/5 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/5 dark:border-white/5 text-left text-xs text-neutral-400 uppercase tracking-wide">
                <th className="px-4 py-3 font-semibold">Gambar</th>
                <th className="px-4 py-3 font-semibold">Judul</th>
                <th className="px-4 py-3 font-semibold">Urutan</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered?.map((item) => (
                <tr key={item.id} className="border-b last:border-0 border-black/5 dark:border-white/5">
                  <td className="px-4 py-3">
                    {item.image ? (
                      <img src={uploadUrl(item.image)} className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-300">
                        <span className="material-symbols-outlined text-xl">image</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-semibold text-neutral-900 dark:text-white max-w-xs truncate">{item.title}</td>
                  <td className="px-4 py-3 text-neutral-500 dark:text-neutral-400">#{item.sort_order}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${item.is_active ? 'bg-forest-500/10 text-forest-500' : 'bg-neutral-100 text-neutral-400 dark:bg-neutral-800'}`}>
                      {item.is_active ? 'Aktif' : 'Nonaktif'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2 justify-end">
                      <Link to={`/admin/carousel/${item.id}/edit`} className={`text-xs font-semibold px-3 py-1.5 rounded-lg bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-white ${MORPH_BTN_SM}`}>Edit</Link>
                      <button onClick={() => remove(item)} className={`text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-50 text-red-500 dark:bg-red-950/30 ${MORPH_BTN_SM}`}>Hapus</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}
